/**
 * Policies feature — queries + mutations.
 *
 * Backed by internal/tenant/condaccess.Handler, mounted at
 * /api/v1/tenants/{tenant_id}/conditional-access/policies
 * under the platform-admin gate.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  policiesListResponseSchema,
  policySchema,
  type CreatePolicyInput,
  type PoliciesListResponse,
  type Policy,
  type PolicyState,
  type ReorderPoliciesInput,
  type UpdatePolicyInput,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/conditional-access/policies`;
}

// ── Fetchers ──────────────────────────────────────────────────────────────

export async function fetchPoliciesList(
  tenantID: string,
): Promise<PoliciesListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return policiesListResponseSchema.parse(raw);
}

export async function fetchPolicy(
  tenantID: string,
  policyId: string,
): Promise<Policy> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(policyId)}`,
  );
  return policySchema.parse(raw);
}

// ── Queries ───────────────────────────────────────────────────────────────

export function usePoliciesQuery(tenantID: string) {
  return useQuery<PoliciesListResponse, ApiError>({
    queryKey: queryKeys.policies.list(tenantID),
    queryFn: () => fetchPoliciesList(tenantID),
    enabled: tenantID.length > 0,
  });
}

export function usePolicyQuery(tenantID: string, policyId: string) {
  return useQuery<Policy, ApiError>({
    queryKey: queryKeys.policies.detail(tenantID, policyId),
    queryFn: () => fetchPolicy(tenantID, policyId),
    enabled: tenantID.length > 0 && policyId.length > 0,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useCreatePolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Policy, ApiError, CreatePolicyInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: input,
        }).then((raw) => policySchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.policies.all(tenantID) });
    },
  });
}

export function useUpdatePolicyMutation(tenantID: string, policyId: string) {
  const qc = useQueryClient();
  return useMutation<Policy, ApiError, UpdatePolicyInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(policyId)}`, {
          method: "PUT",
          body: input,
        }).then((raw) => policySchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.policies.detail(tenantID, policyId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.policies.list(tenantID) });
    },
  });
}

export function useSetPolicyStateMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Policy, ApiError, { policyId: string; state: PolicyState }>({
    mutationFn: ({ policyId, state }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(policyId)}/state`, {
          method: "PATCH",
          body: { state },
        }).then((raw) => policySchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.policies.detail(tenantID, updated.id), updated);
      qc.invalidateQueries({ queryKey: queryKeys.policies.list(tenantID) });
    },
  });
}

export function useDeletePolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (policyId) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(policyId)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: (_res, policyId) => {
      qc.removeQueries({ queryKey: queryKeys.policies.detail(tenantID, policyId) });
      qc.invalidateQueries({ queryKey: queryKeys.policies.list(tenantID) });
    },
  });
}

interface ReorderContext {
  previous: PoliciesListResponse | undefined;
}

export function useReorderPoliciesMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<PoliciesListResponse, ApiError, ReorderPoliciesInput, ReorderContext>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/reorder`, {
          method: "POST",
          body: input,
        }).then((raw) => policiesListResponseSchema.parse(raw)),
      ),
    onMutate: async ({ policy_ids }) => {
      await qc.cancelQueries({ queryKey: queryKeys.policies.list(tenantID) });
      const previous = qc.getQueryData<PoliciesListResponse>(
        queryKeys.policies.list(tenantID),
      );
      if (previous) {
        const byId = new Map(previous.data.map((p) => [p.id, p]));
        const reordered = policy_ids
          .map((id) => byId.get(id))
          .filter((p): p is Policy => p !== undefined)
          .map((p, idx) => ({ ...p, priority: idx }));
        qc.setQueryData<PoliciesListResponse>(
          queryKeys.policies.list(tenantID),
          { data: reordered },
        );
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKeys.policies.list(tenantID), context.previous);
      }
    },
    onSuccess: (server) => {
      qc.setQueryData(queryKeys.policies.list(tenantID), server);
    },
  });
}
